/**
 * Lane C0-OUTFIT2X: the figure composed with parts painted natively on the figure grid (two canvas pixels per art
 * pixel). It is realism.ts `composeReal` (kept unchanged for rollback) plus one step: a piece with a `native` painter
 * is drawn alone at art resolution in role colours (its shape, from the existing garment code, so it fits every pose,
 * view and the cloth's sway), enlarged with Scale2x, and then painted pixel by pixel at full resolution by its painter
 * (materials, folds, trims, patterns, ornaments, light from the side the frame will be seen lit from). Pieces without
 * a painter go exactly as in `composeReal`.
 */
import { PAD_TOP } from '../../sprites';
import { atRes, canvas, ctx2d, selOutline, thinLines, toArtRes } from '../../pixel';
import { FRAME, type Fit, type Layer, type Piece } from '../figure';
import { BARE_ANCHORS } from '../hair';
import type { Skeleton } from '../skeleton';

/** What a native painter sees and may write (all in canvas pixels of the enlarged frame). */
export interface NativeCtx {
  R: number;
  W: number;
  H: number;
  /** Art x / y (the skeleton's) to canvas x / y. */
  X: (x: number) => number;
  Y: (y: number) => number;
  sk: Skeleton;
  /** The screen side the light comes from in this drawing: 1 right (the game's), -1 on a left view (it is flipped). */
  light: 1 | -1;
  z: number;
  /** The piece alone, enlarged: its role colours (0 outside), whole even where something lies in front of it. */
  img: Uint32Array;
  /** `img`'s place in the frame. */
  bx: number;
  by: number;
  bw: number;
  bh: number;
  /** The piece's colour at a frame pixel (0 outside it). */
  at: (x: number, y: number) => number;
  /** The frame pixel shows this piece. */
  vis: (x: number, y: number) => boolean;
  /** Something nearer than this piece shows at a frame pixel. */
  nearer: (x: number, y: number) => boolean;
  /** The frame's colour at a pixel as enlarged (before the line pass), and as it is now. */
  src: (x: number, y: number) => number;
  get: (x: number, y: number) => number;
  /** Paints a pixel this piece shows. */
  set: (x: number, y: number, rgba: number) => void;
  /** Lays a pixel of an ornament anywhere it is not hidden by something nearer (`dz` over the piece's depth). */
  lay: (x: number, y: number, rgba: number, dz?: number) => void;
}

export type NativePaint = (c: NativeCtx) => void;

/** Review tools only: leave the role colours unpainted (to see what each part was drawn as). */
let raw = false;
export function setNativeRaw(v: boolean): void {
  raw = v;
}

interface Pooled {
  owner?: Int16Array;
  depth?: Float32Array;
  pid?: Int16Array;
}
const POOL: Pooled = {};

/** Scale2x of a little-endian RGBA region (w × h) into a new (2w × 2h) array. */
function scale2x(px: Uint32Array, w: number, h: number): Uint32Array {
  const out = new Uint32Array(w * h * 4);
  const W2 = w * 2;
  for (let y = 0; y < h; y++)
    for (let x = 0; x < w; x++) {
      const P = y * w + x;
      const a = px[y > 0 ? P - w : P]!;
      const b = px[x < w - 1 ? P + 1 : P]!;
      const c = px[x > 0 ? P - 1 : P]!;
      const d = px[y < h - 1 ? P + w : P]!;
      const p = px[P]!;
      const j = y * 2 * W2 + x * 2;
      out[j] = c === a && c !== d && a !== b ? a : p;
      out[j + 1] = a === b && a !== c && b !== d ? b : p;
      out[j + W2] = d === c && d !== b && c !== a ? c : p;
      out[j + W2 + 1] = b === d && b !== a && d !== c ? d : p;
    }
  return out;
}

/** Whether any of the layers paints natively (only then is `composeNative` needed). */
export const hasNative = (layers: readonly Layer[]): boolean => layers.some((l) => l.native);

/** One frame of a figure: as `composeReal`, with native painters at double resolution. */
export function composeNative(layers: readonly Layer[], sk: Skeleton, res: 1 | 2 = 1): HTMLCanvasElement {
  // At art resolution the native parts are painted at two and brought down as the 1x mode does.
  if (res === 1) return toArtRes(composeNative(layers, sk, 2));
  let W = FRAME.w;
  let H = FRAME.h;
  let c = canvas(W, H);
  let g = ctx2d(c);
  const R = 2;
  const sg = ctx2d(canvas(W, H));
  const scratch = sg.canvas;
  const fit: Fit = {
    anchors: layers.find((l) => l.anchors)?.anchors?.(sk.view) ?? BARE_ANCHORS,
    hidden: new Set(layers.flatMap((l) => l.hides ?? [])),
  };
  const late = (l: Layer) => l.id === 'weapon';
  const all = layers.flatMap((l, k) => (late(l) ? [] : l.pieces(sk, fit).map((p) => ({ p, k }))));
  const fine = all.some(({ p }) => p.fine);
  const pieces = [...all];
  pieces.sort((a, b) => a.p.z - b.p.z);
  let owner = (POOL.owner = POOL.owner?.length === W * H ? POOL.owner : new Int16Array(W * H)).fill(-1);
  let depth = (POOL.depth = POOL.depth?.length === W * H ? POOL.depth : new Float32Array(W * H)).fill(-Infinity);
  const pid = (POOL.pid = POOL.pid?.length === W * H ? POOL.pid : new Int16Array(W * H)).fill(-1);
  const natives: { n: number; p: Piece; k: number; img: Uint32Array; bx: number; by: number; bw: number; bh: number }[] = [];
  for (let n = 0; n < pieces.length;) {
    const k = pieces[n]!.k;
    sg.setTransform(1, 0, 0, 1, 0, 0);
    sg.clearRect(0, 0, W, H);
    sg.translate(0, PAD_TOP);
    let z = -Infinity;
    const first = n;
    const solo = !!pieces[n]!.p.native;
    for (; n < pieces.length && pieces[n]!.k === k; n++) {
      if ((pieces[n]!.p.hand || pieces[n]!.p.native) && z > -Infinity) break;
      pieces[n]!.p.draw(sg);
      z = pieces[n]!.p.z;
      if (pieces[n]!.p.hand || pieces[n]!.p.native) {
        n++;
        break;
      }
    }
    sg.setTransform(1, 0, 0, 1, 0, 0);
    const d32 = new Uint32Array(sg.getImageData(0, 0, W, H).data.buffer);
    let x0 = W;
    let y0 = H;
    let x1 = -1;
    let y1 = -1;
    for (let i = 0; i < W * H; i++)
      if (d32[i]! >>> 24) {
        owner[i] = k;
        depth[i] = z;
        pid[i] = solo ? first : -1;
        if (solo) {
          const x = i % W;
          const y = (i - x) / W;
          if (x < x0) x0 = x;
          if (x > x1) x1 = x;
          if (y < y0) y0 = y;
          if (y > y1) y1 = y;
        }
      }
    if (solo && x1 >= 0) {
      // The piece alone, a pixel of margin round it, enlarged on its own.
      x0 = Math.max(0, x0 - 1);
      y0 = Math.max(0, y0 - 1);
      x1 = Math.min(W - 1, x1 + 1);
      y1 = Math.min(H - 1, y1 + 1);
      const w = x1 - x0 + 1;
      const h = y1 - y0 + 1;
      const part = new Uint32Array(w * h);
      for (let y = 0; y < h; y++) part.set(d32.subarray((y0 + y) * W + x0, (y0 + y) * W + x0 + w), y * w);
      natives.push({ n: first, p: pieces[first]!.p, k, img: scale2x(part, w, h), bx: x0 * 2, by: y0 * 2, bw: w * 2, bh: h * 2 });
    }
    g.drawImage(scratch, 0, 0);
  }
  {
    const src = g.getImageData(0, 0, W, H);
    const px32 = new Uint32Array(src.data.buffer);
    const W2 = W * 2;
    const H2 = H * 2;
    const out = new ImageData(W2, H2);
    const o32 = new Uint32Array(out.data.buffer);
    const own2 = new Int16Array(W2 * H2);
    const dep2 = new Float32Array(W2 * H2);
    const pid2 = new Int16Array(W2 * H2);
    const copy = (j: number, from: number) => {
      o32[j] = px32[from]!;
      own2[j] = owner[from]!;
      dep2[j] = depth[from]!;
      pid2[j] = pid[from]!;
    };
    for (let y = 0; y < H; y++)
      for (let x = 0; x < W; x++) {
        const P = y * W + x;
        const A = y > 0 ? P - W : P;
        const B = x < W - 1 ? P + 1 : P;
        const C = x > 0 ? P - 1 : P;
        const D = y < H - 1 ? P + W : P;
        const a = px32[A];
        const b = px32[B];
        const cc = px32[C];
        const dd = px32[D];
        const j = y * 2 * W2 + x * 2;
        copy(j, cc === a && cc !== dd && a !== b ? A : P);
        copy(j + 1, a === b && a !== cc && b !== dd ? B : P);
        copy(j + W2, dd === cc && dd !== b && cc !== a ? C : P);
        copy(j + W2 + 1, b === dd && b !== a && dd !== cc ? D : P);
      }
    const pre = o32.slice();
    thinLines(
      o32,
      W2,
      H2,
      2,
      own2,
      dep2,
      layers.findIndex((l) => l.id === 'face'),
    );
    // The native painters, back to front.
    const light: 1 | -1 = sk.left ? -1 : 1;
    const X = (x: number) => Math.round(x * R);
    const Y = (y: number) => Math.round((y + PAD_TOP) * R);
    for (const nv of natives) {
      const { n, p, k, img, bx, by, bw, bh } = nv;
      const inF = (x: number, y: number) => x >= 0 && y >= 0 && x < W2 && y < H2;
      const ctx: NativeCtx = {
        R,
        W: W2,
        H: H2,
        X,
        Y,
        sk,
        light,
        z: p.z,
        img,
        bx,
        by,
        bw,
        bh,
        at: (x, y) => {
          const lx = x - bx;
          const ly = y - by;
          return lx >= 0 && ly >= 0 && lx < bw && ly < bh ? img[ly * bw + lx]! : 0;
        },
        vis: (x, y) => inF(x, y) && pid2[y * W2 + x] === n,
        src: (x, y) => (inF(x, y) ? pre[y * W2 + x]! : 0),
        get: (x, y) => (inF(x, y) ? o32[y * W2 + x]! : 0),
        nearer: (x, y) => {
          if (!inF(x, y)) return false;
          const i = y * W2 + x;
          return pid2[i] !== n && dep2[i]! > p.z + 1e-6 && o32[i]! >>> 24 > 0;
        },
        set: (x, y, rgba) => {
          if (!inF(x, y)) return;
          const i = y * W2 + x;
          if (pid2[i] === n) o32[i] = rgba;
        },
        lay: (x, y, rgba, dz = 0.001) => {
          if (!inF(x, y)) return;
          const i = y * W2 + x;
          const z = p.z + dz;
          if (dep2[i]! > z) return;
          o32[i] = rgba;
          dep2[i] = z;
          own2[i] = k;
          pid2[i] = n;
        },
      };
      if (!raw) p.native!(ctx);
    }
    W = W2;
    H = H2;
    owner = own2;
    depth = dep2;
    c = canvas(W, H);
    g = ctx2d(c);
    g.putImageData(out, 0, 0);
  }
  const fineMask = new Uint8Array(W * H);
  const gripMask = new Uint8Array(W * H);
  let grips = false;
  if (fine) {
    const bodyK = layers.findIndex((l) => l.id === 'body');
    const bare = new Set(['body', 'face', 'hair', 'weapon', 'items', 'tool']);
    const clothed = layers.some((l) => !bare.has(l.id) && !l.id.startsWith('headwear:'));
    const cur = g.getImageData(0, 0, W, H);
    const c32 = new Uint32Array(cur.data.buffer);
    const fines = all.filter(({ p }) => p.fine).sort((x, y) => x.p.z - y.p.z);
    const fg = ctx2d(canvas(W, H));
    for (const { p, k } of fines) {
      fg.setTransform(1, 0, 0, 1, 0, 0);
      fg.clearRect(0, 0, W, H);
      fg.translate(0, PAD_TOP * R);
      p.fine!(fg, R);
      const f32 = new Uint32Array(fg.getImageData(0, 0, W, H).data.buffer);
      const loose = !clothed || layers[k]!.id === 'face';
      for (let i = 0; i < W * H; i++) {
        if (!(f32[i]! >>> 24)) continue;
        const o = owner[i]!;
        const behind = loose && depth[i]! < p.z;
        if (p.hand ? depth[i]! > p.z + 1e-4 : o >= 0 ? o !== bodyK && !fineMask[i] && !behind : !loose) continue;
        c32[i] = f32[i]!;
        owner[i] = bodyK;
        depth[i] = p.z;
        fineMask[i] = 1;
        if (p.grip) gripMask[i] = 1;
      }
      grips ||= !!p.grip;
    }
    g.putImageData(cur, 0, 0);
  }
  const done = c;
  {
    const fitR: Fit = { ...fit, res: R };
    const dg = ctx2d(done);
    const held = grips ? dg.getImageData(0, 0, W, H) : undefined;
    let wg: CanvasRenderingContext2D | undefined;
    const latePieces = layers.filter(late).flatMap((l) => l.pieces(sk, fitR));
    if (latePieces.length) layLate(dg, latePieces, depth, owner, (wg ??= ctx2d(canvas(W, H))), W, H, R);
    if (held && wg) {
      const now = dg.getImageData(0, 0, W, H);
      const n32 = new Uint32Array(now.data.buffer);
      const h32 = new Uint32Array(held.data.buffer);
      for (let i = 0; i < W * H; i++) if (gripMask[i]) n32[i] = h32[i]!;
      dg.putImageData(now, 0, 0);
    }
  }
  return atRes(done, R);
}

/** realism.ts `layLate` (not exported there): the weapon laid in against the figure's depth. */
function layLate(
  dg: CanvasRenderingContext2D,
  pieces: readonly Piece[],
  depth: Float32Array,
  owner: Int16Array,
  wg: CanvasRenderingContext2D,
  W: number,
  H: number,
  R: number,
): void {
  const cur = dg.getImageData(0, 0, W, H);
  const c32 = new Uint32Array(cur.data.buffer);
  const N = W * H;
  const lz = new Float32Array(N);
  const mark = new Uint8Array(N);
  const body = (i: number) => owner[i]! >= 0 && Number.isFinite(depth[i]!);
  for (let i = 0; i < N; i++) {
    if (!(c32[i]! >>> 24)) lz[i] = -Infinity;
    else if (body(i)) lz[i] = depth[i]!;
    else {
      let z = -Infinity;
      const x = i % W;
      if (x > 0 && body(i - 1)) z = Math.max(z, depth[i - 1]!);
      if (x < W - 1 && body(i + 1)) z = Math.max(z, depth[i + 1]!);
      if (i >= W && body(i - W)) z = Math.max(z, depth[i - W]!);
      if (i < N - W && body(i + W)) z = Math.max(z, depth[i + W]!);
      lz[i] = z;
    }
  }
  const top = PAD_TOP * R;
  for (const p of [...pieces].sort((a, b) => a.z - b.z)) {
    wg.setTransform(1, 0, 0, 1, 0, 0);
    wg.clearRect(0, 0, W, H);
    wg.translate(0, top);
    p.draw(wg);
    const o32 = new Uint32Array(ctx2d(selOutline(wg.canvas)).getImageData(0, 0, W, H).data.buffer);
    for (let i = 0; i < N; i++) {
      if (!(o32[i]! >>> 24)) continue;
      const z = p.depth ? p.depth((i % W) + 0.5, Math.floor(i / W) + 0.5 - top) : p.z;
      if (z < lz[i]!) continue;
      c32[i] = o32[i]!;
      mark[i] = Number.isFinite(lz[i]!) ? 2 : 1;
      lz[i] = z;
    }
  }
  const d8 = cur.data;
  for (let i = 0; i < N; i++) {
    if (mark[i] !== 2) continue;
    const x = i % W;
    if (x < R) continue;
    for (const dy of [R, 2 * R]) {
      const t = i + dy * W - R;
      if (t >= N || mark[t] || owner[t]! < 0) continue;
      mark[t] = 3;
      const q = t * 4;
      const k = dy === R ? 0.7 : 0.82;
      d8[q] = d8[q]! * k + 44 * (1 - k) * 0.45;
      d8[q + 1] = d8[q + 1]! * k + 40 * (1 - k) * 0.45;
      d8[q + 2] = d8[q + 2]! * k + 96 * (1 - k) * 0.45;
    }
  }
  dg.putImageData(cur, 0, 0);
}
